import { createContext, createElement, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiService, type ApiError, type SystemSettingsResponse } from '../services/api';
import { SYSTEM_SETTINGS_QUERY_KEY } from '../types/systemSettings';
import {
  applySystemTheme,
  applySystemThemeFromDefaults,
  type ThemeMode,
  type ThemePreference,
} from '../utils/applySystemTheme';

interface SystemThemeProviderProps {
  enabled?: boolean;
  children: ReactNode;
}

const THEME_STORAGE_KEY = 'slyrsa-theme-preference';
const DEFAULT_PREFERENCE: ThemePreference = 'light';

const readStoredPreference = (): ThemePreference => {
  if (typeof window === 'undefined') {
    return DEFAULT_PREFERENCE;
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return DEFAULT_PREFERENCE;
};

const getSystemMode = (): ThemeMode => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const resolveEffectiveMode = (preference: ThemePreference, systemMode: ThemeMode): ThemeMode => {
  return preference === 'system' ? systemMode : preference;
};

type SystemThemeQueryResult = UseQueryResult<SystemSettingsResponse, ApiError>;

export type UseSystemThemeResult = SystemThemeQueryResult & {
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  effectiveMode: ThemeMode;
};

const SystemThemeContext = createContext<UseSystemThemeResult | undefined>(undefined);

const useProvideSystemTheme = (enabled: boolean | undefined): UseSystemThemeResult => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [systemMode, setSystemMode] = useState<ThemeMode>(() => getSystemMode());
  const effectiveMode = resolveEffectiveMode(themePreference, systemMode);

  const queryResult = useQuery<SystemSettingsResponse, ApiError, SystemSettingsResponse, typeof SYSTEM_SETTINGS_QUERY_KEY>({
    queryKey: SYSTEM_SETTINGS_QUERY_KEY,
    queryFn: () => apiService.getSystemSettings(),
    enabled: Boolean(enabled),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (failureCount, err) => {
      if (err.statusCode === 401 || err.statusCode === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange);
    } else if (typeof media.addListener === 'function') {
      media.addListener(handleChange);
    }

    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', handleChange);
      } else if (typeof media.removeListener === 'function') {
        media.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (Boolean(enabled) && queryResult.data) {
      applySystemTheme(queryResult.data, effectiveMode);
    } else {
      applySystemThemeFromDefaults(effectiveMode);
    }
  }, [enabled, queryResult.data, effectiveMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }, [themePreference]);

  const setThemePreference = useCallback((preference: ThemePreference) => {
    setThemePreferenceState(preference);
  }, []);

  return {
    ...queryResult,
    themePreference,
    setThemePreference,
    effectiveMode,
  };
};

export const SystemThemeProvider = ({ children, enabled = true }: SystemThemeProviderProps) => {
  const value = useProvideSystemTheme(enabled);
  return createElement(SystemThemeContext.Provider, { value }, children);
};

export const useSystemTheme = (): UseSystemThemeResult => {
  const context = useContext(SystemThemeContext);
  if (!context) {
    throw new Error('useSystemTheme must be used within a SystemThemeProvider');
  }
  return context;
};

export type { ThemePreference, ThemeMode };
